import React, { useMemo } from 'react';
import BaseWidget from './BaseWidget';
import { hasDashboardSourceError, shapeActivityFeed } from '../../lib/dashboardMetrics';

/**
 * RecentActivityWidget Component
 *
 * Displays a feed of recent business activities
 */
const RecentActivityWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, theme = 'light', metrics }) => {
  const {
    maxItems = 10,
    activityTypes = ['all'],
    showTimestamp = true,
    activities = []
  } = config;

  // BL-194 Gate 1: the feed reads real events (invoices, payments, bookings,
  // new accounts) from dashboard_recent_activity. The hardcoded "Acme Corp"
  // sample list this widget used to show is gone; an empty tenant now correctly
  // shows the existing empty state.
  const activityData = useMemo(() => {
    if (activities && activities.length > 0) {
      return activities.slice(0, maxItems);
    }
    return shapeActivityFeed(metrics?.recentActivity || [], maxItems, activityTypes);
  }, [activities, maxItems, activityTypes, metrics]);
  const sourceUnavailable = !(activities && activities.length > 0)
    && hasDashboardSourceError(metrics, 'dashboard_recent_activity');

  // Format timestamp as relative time
  const formatTimestamp = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Color classes
  const colorClasses = {
    blue: 'bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)]',
    green: 'bg-[var(--ui-positive)]/10 text-[var(--ui-positive)]',
    purple: 'bg-[var(--rule-magenta)]/10 text-[var(--rule-magenta)]',
    orange: 'bg-[var(--ui-moderate)]/10 text-[var(--ui-moderate)]',
    red: 'bg-[var(--ui-alert)]/10 text-[var(--ui-alert)]'
  };

  return (
    <BaseWidget
      id={id}
      title="Recent Activity"
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      }
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '100%' }}>
        {activityData.length === 0 || sourceUnavailable ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <svg className="w-12 h-12 text-[var(--ink-muted)] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm text-[var(--ink-muted)]" role="status">
              {sourceUnavailable ? 'Activity data temporarily unavailable' : 'No recent activity'}
            </p>
          </div>
        ) : (
          activityData.map(activity => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-2 rounded-lg hover:bg-[var(--surface-sunken)] transition-colors cursor-pointer"
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClasses[activity.color] || colorClasses.blue}`}>
                <span className="text-sm">{activity.icon}</span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--ink)] truncate">
                  {activity.title}
                </p>
                <p className="text-xs text-[var(--ink-muted)] truncate">
                  {activity.description}
                </p>
                {showTimestamp && (
                  <p className="text-xs text-[var(--ink-muted)] mt-0.5">
                    {formatTimestamp(activity.timestamp)}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </BaseWidget>
  );
};

export default RecentActivityWidget;
